import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CircularProgress, Box, Alert } from '@mui/material';
import api from '../../services/api';
import { RootState } from '../../store';

interface DashboardTypeResponse {
  user_id: string;
  email: string;
  name: string;
  dashboard_type: 'admin' | 'pilot' | 'club_manager' | 'basic';
  redirect_path: string;
  permissions: string[];
  message: string;
  available_roles?: ('admin' | 'club_manager' | 'pilot')[];
  is_dual_role?: boolean;
  login_mode?: string;
}

interface SecureDashboardRouterProps {
  children: React.ReactNode;
  requiredDashboardType?: 'admin' | 'pilot' | 'club_manager' | 'basic';
}

const SecureDashboardRouter: React.FC<SecureDashboardRouterProps> = ({
  children,
  requiredDashboardType,
}) => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    // Only check permissions once on mount
    if (hasChecked) return;

    const checkDashboardPermissions = async () => {
      try {
        setLoading(true);
        setError(null);

        // Pass the login_mode from the user state to the backend
        const loginMode = user?.login_mode;
        const url = loginMode
          ? `/me/dashboard-type?login_mode=${loginMode}`
          : '/me/dashboard-type';

        // Get dashboard type from backend - single source of truth
        const response = await api.get<DashboardTypeResponse>(url);
        const info = response.data;

        // If a specific dashboard type is required, check permissions
        if (requiredDashboardType) {
          // Admin can access everything
          if (info.dashboard_type === 'admin') {
            // Admin user - granting access to all dashboards
          }
          // Club managers can access club dashboard
          else if (
            requiredDashboardType === 'club_manager' &&
            info.dashboard_type === 'club_manager'
          ) {
            // Club manager accessing club dashboard - allowed
          }
          // Pilots can only access pilot dashboard
          else if (
            requiredDashboardType === 'pilot' &&
            info.dashboard_type === 'pilot'
          ) {
            // Pilot accessing pilot dashboard - allowed
          }
          // For dual-role users, allow access based on their available_roles
          else if (
            info.is_dual_role &&
            info.available_roles?.includes(requiredDashboardType as 'admin' | 'club_manager' | 'pilot')
          ) {
            // Dual-role user accessing one of their available dashboards - allowed
          }
          // Otherwise deny access and redirect
          else {
            setHasChecked(true); // Mark as checked BEFORE navigating
            setLoading(false);
            // Only redirect if we're not already at the correct path
            if (window.location.pathname !== info.redirect_path) {
              navigate(info.redirect_path);
            }
            return;
          }
        }

        setLoading(false);
        setHasChecked(true);
      } catch (err: any) {
        setError('Failed to verify permissions. Please try logging in again.');
        setLoading(false);
        setHasChecked(true); // Mark as checked even on error

        // On error, redirect to landing page
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    };

    checkDashboardPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChecked, requiredDashboardType, user?.login_mode]); // Navigate excluded to prevent infinite loops

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        p={3}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Render children only if permissions are verified
  return <>{children}</>;
};

export default SecureDashboardRouter;